import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Table, Typography, Space, Select, Tooltip, Descriptions } from 'antd';
import { InfoCircleOutlined, FolderAddOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import ExpandCollapseAllButton from '../../common/ExpandCollapseAllButton';
import NumberInput from '../../common/NumberInput';
import { useTender } from '../../../contexts/TenderContext';
import { useDevMode } from '@/contexts/DevModeContext';
import { useCanConfigureBoq } from '@/hooks/useCanConfigureBoq';
import { useItemLibrary } from '../../../hooks/useItemLibrary';
import { useRoadsAndSewersMarkupFactor } from '../../../hooks/useRoadsAndSewersMarkupFactor';
import { useBoqLatestVersion } from '../../../hooks/useBillOfQuantities';
import { flattenBoqSummaryLineItems } from '../../../utils/roadsAndSewersBoqSummaryComputer';
import { BcCalculationDetails, computeBcCalculation } from '../../common/BcCalculationDetails';
import type { BoqSummaryLineItem } from '../../../utils/groundworksBoqSummaryComputer';
import { COLORS, formatUnit, MAX_SECTION_NESTING_DEPTH, MAX_SECTION_NESTING_DEPTH_MESSAGE, buildSectionDepthMap } from '../../../utils/constants';
import { COMPOUND_ROW_DEFS, getCompoundLabels } from '../../../utils/compoundRowDefs';
import { findCompoundEvaluateCode } from '../../../utils/compoundEvaluateCodesMapping';
import type { CompoundConfig, DynamicBillSummaryNode, DynamicBillSummaryItemNode, DynamicBillSummarySectionNode } from '../../../types/tender.types';
import { useBillSummaryExtraItems, buildRootExtraRows, buildNestedExtraRows } from '../../../hooks/useBillSummaryExtraItems';
import { computeDynamicBcCalculation, type DynamicBillSummaryLineItem } from '../../../utils/dynamicBillSummarySerialization';

const { Text, Title } = Typography;

const fmtCurrency = (n: number) =>
  `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface BillRow {
  key: string;
  category: string;
  description: string;
  quantity: number | string;
  unit: string;
  rate: number | string;
  amount: number | string;
  evaluateCode?: string;
  rowType?: 'group-header' | 'sub-header' | 'label' | 'item' | 'main-header' | 'column-type-header' | 'line-item';
  children?: BillRow[];
  sectionAmount?: number;
  isExtra?: boolean;
  attachKey?: string;
  nodeType?: 'section' | 'item';
  amountType?: 'number' | 'text';
  amountText?: string;
  dbRate?: string;
}

const findExtraNode = (nodes: DynamicBillSummaryNode[], id: string): DynamicBillSummaryNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.type === 'section') {
      const found = findExtraNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

const CompoundBillSummary: React.FC = () => {
  const showDevMode = useDevMode();
  const dynamicSummaryEnabled = useCanConfigureBoq();
  const { state, updateField } = useTender();
  const { data: itemLibraryData } = useItemLibrary(state.region || '');
  // Same OH&P/Preliminaries markup every R&S Bill Summary applies to its own
  // Rate column now — baked into rateMap below so every downstream lookup
  // (Rate/Amount columns, footer, extra items) picks it up for free.
  const markupFactor = useRoadsAndSewersMarkupFactor();
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  // SummaryKey modal — "BCCalculation" independent backend-dbRate-only recomputation,
  // same as groundworks Bill Summaries (BcCalculationDetails.tsx / computeBcCalculation).
  const [summaryKeyModalRecord, setSummaryKeyModalRecord] = useState<BoqSummaryLineItem | null>(null);
  const [extraSummaryKeyRecord, setExtraSummaryKeyRecord] = useState<DynamicBillSummaryLineItem | null>(null);

  const compoundExtraItems = state.compoundExtraItems;
  const [newlyAddedRowId, setNewlyAddedRowId] = useState<string | null>(null);
  useEffect(() => {
    if (!newlyAddedRowId) return;
    const timer = setTimeout(() => setNewlyAddedRowId(null), 2000);
    return () => clearTimeout(timer);
  }, [newlyAddedRowId]);
  const extraItemsApi = useBillSummaryExtraItems({
    extraItems: compoundExtraItems,
    onChange: (updated) => updateField('compoundExtraItems', updated),
    itemLibraryData,
    enableRunMatrixMapping: true,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) setExpandedRowKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      setNewlyAddedRowId(nodeId);
    },
  });

  // Rate is rounded to 2dp here — the SAME value shown in the Rate column —
  // so Amount always equals the displayed Rate × displayed Qty exactly (the
  // raw item-library rate can carry more than 2dp, which would otherwise
  // silently drift Amount away from what the user can verify by hand). Same
  // convention every groundworks Bill Summary's Amount column uses.
  const rateMap = useMemo(() => {
    const map: Record<string, number> = {};
    (itemLibraryData ?? []).forEach((item) => {
      if (item.resourceCode) map[item.resourceCode] = parseFloat(((item.nettRate || 0) * markupFactor).toFixed(2));
    });
    return map;
  }, [itemLibraryData, markupFactor]);

  // Plain nett rate, no markup — only for the dev-mode "BC" modal below, which
  // shows the backend's dbRate-only recomputation for reconciliation. Marking
  // this up too would make it trivially agree with the (now marked-up) Rate
  // column and defeat its purpose as an independent check.
  const nettRateMap = useMemo(() => {
    const map: Record<string, number> = {};
    (itemLibraryData ?? []).forEach((item) => {
      if (item.resourceCode) map[item.resourceCode] = parseFloat((item.nettRate || 0).toFixed(2));
    });
    return map;
  }, [itemLibraryData]);

  const savedData = state.compound?.table || {};
  const savedConfig = (state.compound?.config ?? {}) as CompoundConfig;

  // Dev-mode only: pulls the persisted `roadAndSewers.summary.compound` tree
  // from the same boq-versions API the rest of this review page uses, so the
  // SummaryKey column reflects what was actually saved rather than the live
  // recomputation above.
  const { data: latestBoqVersionData } = useBoqLatestVersion(
    state.projectName || undefined,
    { enabled: showDevMode && !!state.projectName, oppNo: state.opportunityNo },
  );
  const persistedCompound = useMemo(
    () => flattenBoqSummaryLineItems(
      (latestBoqVersionData as unknown as { boq?: { billOfQuantities?: Array<{ roadAndSewers?: { summary?: { compound?: import('../../../utils/groundworksBoqSummaryComputer').BoqSummaryLineItem[] } } }> } })
        ?.boq?.billOfQuantities?.[0]?.roadAndSewers?.summary?.compound,
    ),
    [latestBoqVersionData],
  );

  // Configured section headings / row descriptions — shared with the Run Matrix's
  // Compound section so the two can never resolve different evaluate codes.
  const { sectionLabels, itemLabels } = useMemo(() => getCompoundLabels(savedConfig), [savedConfig]);

  const { processedData, grandTotal } = useMemo(() => {
    const groups: BillRow[] = [];
    let total = 0;
    let groupNum = 0;
    let currentGroup: { key: string; description: string; sections: BillRow[] } | null = null;
    let currentSection: { key: string; description: string; items: BillRow[] } | null = null;

    const flushSection = () => {
      if (!currentGroup || !currentSection || currentSection.items.length === 0) return;
      const sectionAmount = currentSection.items.reduce(
        (s, item) => s + (typeof item.amount === 'number' ? item.amount : 0), 0,
      );
      currentGroup.sections.push({
        key: currentSection.key,
        category: '',
        description: currentSection.description,
        quantity: '', unit: '', rate: '', amount: '',
        rowType: 'sub-header',
        sectionAmount,
        children: currentSection.items,
      });
    };

    const flushGroup = () => {
      flushSection();
      currentSection = null;
      if (!currentGroup || currentGroup.sections.length === 0) return;
      const groupAmount = currentGroup.sections.reduce((s, sec) => s + (sec.sectionAmount || 0), 0);
      groupNum++;
      groups.push({
        key: currentGroup.key,
        category: `${groupNum}.0`,
        description: currentGroup.description,
        quantity: '', unit: '', rate: '', amount: '',
        rowType: 'group-header',
        sectionAmount: groupAmount,
        children: currentGroup.sections,
      });
      total += groupAmount;
    };

    COMPOUND_ROW_DEFS.forEach((row) => {
      if (row.rowType === 'compoundHeader') {
        flushGroup();
        currentGroup = { key: row.id, description: row.description, sections: [] };
        return;
      }
      if (row.rowType === 'sectionHeader') {
        flushSection();
        currentSection = { key: row.id, description: sectionLabels[row.id] ?? row.description, items: [] };
        return;
      }
      if (row.rowType === 'subHeader') {
        currentSection?.items.push({
          key: row.id, category: '', description: row.description,
          quantity: '', unit: '', rate: '', amount: '', rowType: 'label',
        });
        return;
      }
      const saved = savedData[row.id];
      const qty = saved?.quant ?? 0;
      if (!qty || !currentSection) return;
      const description = itemLabels[row.id] ?? row.description;
      // `saved.evaluateCode` is never actually persisted (Compound.tsx's `updateRow` only
      // ever writes `quant` — its own dev-mode column reads the same dead field, so it's
      // always blank there too) — same bug class Foul's Pump Station/Substation/POS had.
      // Resolved live via `findCompoundEvaluateCode` instead, though the source spec's own
      // 'Compound' sheet has no codes assigned to any row yet, so this currently always
      // returns undefined — Rate/Amount blank is correct, not a residual bug.
      const evaluateCode = findCompoundEvaluateCode(description);
      const rate = evaluateCode ? (rateMap[evaluateCode] ?? 0) : 0;
      currentSection.items.push({
        key: row.id,
        category: String(row.itemNo ?? ''),
        description,
        quantity: qty,
        unit: row.unit,
        rate,
        amount: qty * rate,
        evaluateCode,
        rowType: 'item',
      });
    });
    flushGroup();

    // Graft ad-hoc extra rows on top of this otherwise-immutable computed
    // tree — same convention every groundworks Bill Summary follows, marked
    // up the same way this table's own Rate column now is. This table's own
    // sub-header rows carry an empty `category`, so nested extra rows fall
    // back to "0" as their base instead of an empty string.
    const extraBuckets = compoundExtraItems ?? {};
    const consumedAttachKeys = new Set<string>();
    const injectExtraRows = (rowsIn: BillRow[]): BillRow[] =>
      rowsIn.map((row) => {
        const bucket = extraBuckets[row.key];
        const existingChildren: BillRow[] = row.children ?? [];
        const ownChildren = row.children ? injectExtraRows(row.children) : undefined;
        if (!bucket || bucket.length === 0) {
          return ownChildren ? { ...row, children: ownChildren } : row;
        }
        consumedAttachKeys.add(row.key);
        const extraRows = buildNestedExtraRows(bucket, row.key, row.category || '0', existingChildren.length, markupFactor);
        const mergedChildren = [...(ownChildren ?? []), ...extraRows];
        return {
          ...row,
          children: mergedChildren,
          ...(row.sectionAmount !== undefined && {
            sectionAmount: mergedChildren.reduce((sum, c) => sum + (parseFloat(String(c.amount || '0')) || 0), 0),
          }),
        };
      });
    const injectedRows = injectExtraRows(groups);
    let nextRootNumber = groupNum + 1;
    const rootExtraRows = buildRootExtraRows(extraBuckets['__root__'] ?? [], '__root__', nextRootNumber, markupFactor);
    nextRootNumber += rootExtraRows.length;
    const orphanedExtraRows = Object.keys(extraBuckets)
      .filter((key) => key !== '__root__' && !consumedAttachKeys.has(key) && extraBuckets[key].length > 0)
      .map((key) => {
        const n = nextRootNumber++;
        const children = buildNestedExtraRows(extraBuckets[key], key, `${n}.0`, 0, markupFactor);
        return {
          key: `orphaned-${key}`,
          category: `${n}.0`,
          description: 'Additional Items',
          rowType: 'main-header' as const,
          isExtra: true,
          attachKey: key,
          nodeType: 'section' as const,
          quantity: '', unit: '', rate: '', amount: '', dbRate: '',
          sectionAmount: children.reduce((sum, c) => sum + (parseFloat(c.amount || '0') || 0), 0),
          children,
        };
      });
    const finalRows: BillRow[] = [...injectedRows, ...rootExtraRows, ...orphanedExtraRows];

    const sumLeaves = (nodes: BillRow[]): number =>
      nodes.reduce((sum, node) => {
        if (node.children && node.children.length > 0) return sum + sumLeaves(node.children);
        if (node.rowType === 'label' || node.amountType === 'text') return sum;
        const amt = typeof node.amount === 'number' ? node.amount : parseFloat(String(node.amount || '0'));
        return sum + (isNaN(amt) ? 0 : amt);
      }, 0);

    return { processedData: finalRows, grandTotal: sumLeaves(finalRows) };
  }, [savedData, rateMap, sectionLabels, itemLabels, compoundExtraItems, markupFactor]);

  const rowDepthMap = useMemo(() => buildSectionDepthMap(processedData), [processedData]);

  const handleExpandCollapseAll = () => {
    if (expandedRowKeys.length > 0) {
      setExpandedRowKeys([]);
    } else {
      const allKeys: React.Key[] = [];
      const collect = (rows: BillRow[]) => {
        rows.forEach((row) => {
          if (row.children?.length) {
            allKeys.push(row.key);
            collect(row.children);
          }
        });
      };
      collect(processedData);
      setExpandedRowKeys(allKeys);
    }
  };

  const isHeaderRow = (rowType?: BillRow['rowType']) =>
    rowType === 'group-header' || rowType === 'sub-header' || rowType === 'main-header' || rowType === 'column-type-header';

  const columns = [
    {
      title: 'Item',
      dataIndex: 'category',
      key: 'category',
      width: 110,
      align: 'center' as const,
      render: (value: string, record: BillRow) => (isHeaderRow(record.rowType) ? <Text strong>{value}</Text> : value),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (value: string, record: BillRow) => {
        if (record.rowType === 'group-header') return <Text strong className="text-indigo-600">{value.toUpperCase()}</Text>;
        if (record.rowType === 'sub-header' || record.rowType === 'main-header' || record.rowType === 'column-type-header')
          return <Text strong className="text-indigo-600">{value}</Text>;
        if (record.rowType === 'label') return <Text strong className="text-gray-600">{value}</Text>;
        return (
          <div>
            <Text>{value}</Text>
            {showDevMode && record.evaluateCode && (
              <div style={{ fontSize: 10, color: '#999', fontWeight: 500, marginTop: 2, fontFamily: 'monospace' }}>
                {record.evaluateCode}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 110,
      align: 'right' as const,
      render: (value: number | string, record: BillRow) => {
        if (isHeaderRow(record.rowType) || record.rowType === 'label') return '';
        if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
          return (
            <NumberInput
              value={parseFloat(String(value)) || 0}
              onChange={(qty) => extraItemsApi.updateNode(record.attachKey!, record.key, { qty })}
              size="small"
              decimalPlaces={2}
              autoFocus={record.key === newlyAddedRowId}
            />
          );
        }
        const n = typeof value === 'number' ? value : parseFloat(String(value));
        return isNaN(n) ? '' : <div style={{ textAlign: 'right' }}>{n.toLocaleString('en-GB', { maximumFractionDigits: 2 })}</div>;
      },
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 90,
      align: 'center' as const,
      render: (value: string, record: BillRow) => {
        if (isHeaderRow(record.rowType) || record.rowType === 'label') return '';
        if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
          return (
            <Select
              value={value || undefined}
              onChange={(unit) => extraItemsApi.updateNode(record.attachKey!, record.key, { unit })}
              options={extraItemsApi.unitOptions.map((u) => ({ value: u, label: u }))}
              placeholder="Unit"
              size="small"
              showSearch
              allowClear
              style={{ width: '100%' }}
            />
          );
        }
        return formatUnit(value);
      },
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
      width: 110,
      align: 'right' as const,
      render: (value: number | string, record: BillRow) => {
        if (isHeaderRow(record.rowType) || record.rowType === 'label') return '';
        const n = typeof value === 'number' ? value : parseFloat(String(value));
        if (isNaN(n) || n === 0) return '';
        return <div style={{ textAlign: 'right' }}>£{n.toFixed(2)}</div>;
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right' as const,
      render: (value: number | string, record: BillRow) => {
        if (isHeaderRow(record.rowType)) {
          const isExpanded = expandedRowKeys.includes(record.key);
          if (!isExpanded && record.sectionAmount && record.sectionAmount > 0) {
            return <div style={{ textAlign: 'right' }}><span className="font-semibold">{fmtCurrency(record.sectionAmount)}</span></div>;
          }
          return '';
        }
        if (record.rowType === 'label') return '';
        if (record.amountType === 'text') {
          return <div style={{ textAlign: 'right' }}><Text>{record.amountText}</Text></div>;
        }
        const n = typeof value === 'number' ? value : parseFloat(String(value));
        if (isNaN(n) || n === 0) return '';
        return <div style={{ textAlign: 'right' }}>{fmtCurrency(n)}</div>;
      },
    },
    // ── Dev-mode only: persisted-vs-live comparison column ───────────────────
    ...(showDevMode ? [{
      title: 'SummaryKey',
      key: 'summaryKey',
      width: 120,
      align: 'right' as const,
      render: (_value: unknown, record: BillRow) => {
        if (isHeaderRow(record.rowType) || record.rowType === 'label') return '';
        if (record.isExtra) {
          const node = findExtraNode(compoundExtraItems?.[record.attachKey!] ?? [], record.key) as DynamicBillSummaryItemNode | null;
          if (!node) return '';
          const liveAmount = typeof record.amount === 'number' ? record.amount : parseFloat(String(record.amount || '0')) || 0;
          const lineItem: DynamicBillSummaryLineItem = {
            key: node.id, description: node.description, quantity: node.qty, unit: node.unit, rate: node.rate,
            amount: liveAmount, amountType: node.amountType, amountText: node.amountText,
            resourceCode: node.resourceCode, resourceCodes: node.resourceCodes,
          };
          const bc = computeDynamicBcCalculation(lineItem, (code) => nettRateMap[code] ?? 0);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Text style={{ fontSize: 12, color: '#f59e0b' }}>unsaved</Text>
                <InfoCircleOutlined style={{ cursor: 'pointer', color: '#3b82f6' }} onClick={() => setExtraSummaryKeyRecord(lineItem)} />
              </div>
              {bc && <Text style={{ fontSize: 10, color: '#3b82f6' }}>BC: {fmtCurrency(bc.amount)}</Text>}
            </div>
          );
        }
        const persisted = persistedCompound.get(record.key);
        if (!persisted) return <Text style={{ fontSize: 10, color: '#f59e0b' }}>unsaved</Text>;
        const liveAmount = typeof record.amount === 'number' ? record.amount : parseFloat(String(record.amount));
        const mismatch = Math.abs((isNaN(liveAmount) ? 0 : liveAmount) - (persisted.amount || 0)) > 0.01;
        const bcAmount = computeBcCalculation(persisted, (code) => nettRateMap[code] ?? 0).amount;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : '#6b7280' }}>
                {fmtCurrency(persisted.amount || 0)}
              </Text>
              <InfoCircleOutlined
                style={{ cursor: 'pointer', color: '#3b82f6' }}
                onClick={() => setSummaryKeyModalRecord(persisted)}
              />
            </div>
            <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
              BC: {fmtCurrency(bcAmount)}
            </Text>
          </div>
        );
      },
    }] : []),
    ...(dynamicSummaryEnabled && !state.isViewMode ? [{
      title: 'Actions',
      key: 'extraActions',
      width: 150,
      align: 'center' as const,
      render: (_: unknown, record: BillRow) => {
        if (record.isExtra) {
          const node = findExtraNode(compoundExtraItems?.[record.attachKey!] ?? [], record.key);
          if (!node) return '';
          if (record.nodeType === 'section') {
            return (
              <Space size={4}>
                <Tooltip title={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
                  <Button size="small" icon={<FolderAddOutlined />} title="Add Section" disabled={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH} onClick={() => extraItemsApi.openAddSection(record.attachKey!, record.key)} />
                </Tooltip>
                <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.attachKey!, record.key)} />
                <Button size="small" icon={<EditOutlined />} title="Edit Section" onClick={() => extraItemsApi.openEditSection(record.attachKey!, node as DynamicBillSummarySectionNode)} />
                <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey!, record.key)} />
              </Space>
            );
          }
          return (
            <Space size={4}>
              <Button size="small" icon={<EditOutlined />} title="Edit Item" onClick={() => extraItemsApi.openEditItem(record.attachKey!, node as DynamicBillSummaryItemNode)} />
              <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey!, record.key)} />
            </Space>
          );
        }
        if (!isHeaderRow(record.rowType)) return '';
        const atMaxDepth = (rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH;
        return (
          <Space size={4}>
            <Tooltip title={atMaxDepth ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
              <Button size="small" icon={<FolderAddOutlined />} title="Add Section" disabled={atMaxDepth} onClick={() => extraItemsApi.openAddSection(record.key, null)} />
            </Tooltip>
            <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.key, null)} />
          </Space>
        );
      },
    }] : []),
  ];

  const hasData = processedData.length > 0;

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={5} style={{ margin: 0, fontWeight: 600, fontSize: '16px', color: '#111827' }}>
          Compound Bill Summary
        </Title>
        <Space>
          {dynamicSummaryEnabled && !state.isViewMode && (
            <Button size="small" icon={<FolderAddOutlined />} onClick={() => extraItemsApi.openAddSection('__root__', null)}>
              Add Section
            </Button>
          )}
          <ExpandCollapseAllButton expanded={expandedRowKeys.length > 0} onToggle={handleExpandCollapseAll} />
        </Space>
      </div>
      <style>{`
        .compound-bill-table .ant-table { border-radius: 0 !important; }
        .compound-bill-table .ant-table-container { border-radius: 0 !important; }
        .compound-bill-table .ant-table-thead > tr > th { background-color: ${COLORS.tableHeaderBg} !important; }
        /* Explicit :hover so every row visibly highlights on hover, same fix
           as FoulSewersBillSummary.tsx/StormSewersBillSummary.tsx — plain CSS
           :hover applies regardless of any legacy colSpan render props a cell
           might use, unlike antd's own JS-driven hover class. */
        .compound-bill-table .ant-table-tbody > tr:hover > td { background-color: #fafafa !important; }
        .compound-bill-table .ant-table-tbody > tr.bg-indigo-50:hover > td { background-color: #e0e7ff !important; }
        .compound-bill-table .ant-table-tbody > tr.bg-gray-50:hover > td { background-color: #f3f4f6 !important; }
      `}</style>
      {hasData ? (
        <Table
          className="compound-bill-table"
          dataSource={processedData}
          columns={columns}
          rowKey="key"
          bordered
          size="middle"
          expandable={{
            childrenColumnName: 'children',
            defaultExpandAllRows: false,
            expandedRowKeys,
            onExpand: (expanded, record) => {
              setExpandedRowKeys(
                expanded ? [...expandedRowKeys, record.key] : expandedRowKeys.filter((k) => k !== record.key),
              );
            },
          }}
          rowClassName={(record) => {
            const classes: string[] = [];
            if (isHeaderRow(record.rowType)) classes.push('bg-indigo-50', 'font-semibold');
            if (record.key === newlyAddedRowId) classes.push('compound-summary-row-added');
            return classes.join(' ');
          }}
          pagination={false}
          summary={() => (
            <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 700 }}>
              <Table.Summary.Cell index={0} colSpan={2}>
                <Text strong style={{ fontWeight: 700 }}>TOTAL COMPOUND</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} />
              <Table.Summary.Cell index={3} />
              <Table.Summary.Cell index={4} />
              <Table.Summary.Cell index={5} align="right">
                <span style={{ display: 'block', textAlign: 'right', fontWeight: 700 }}>{fmtCurrency(grandTotal)}</span>
              </Table.Summary.Cell>
              {showDevMode && <Table.Summary.Cell index={6} />}
              {dynamicSummaryEnabled && !state.isViewMode && <Table.Summary.Cell index={showDevMode ? 7 : 6} />}
            </Table.Summary.Row>
          )}
        />
      ) : (
        <div style={{ padding: '32px', textAlign: 'center' }}>
          <Text type="secondary">No compound quantities entered. Enter quantities on the Compound page.</Text>
        </div>
      )}
      <style>{`
        @keyframes compound-summary-row-added-fade { 0% { background-color: #fef3c7; } 100% { background-color: transparent; } }
        .compound-summary-row-added > td { animation: compound-summary-row-added-fade 1.8s ease-out; }
      `}</style>
      <Modal
        title="SummaryKey — Amount Calculation"
        open={!!summaryKeyModalRecord}
        onCancel={() => setSummaryKeyModalRecord(null)}
        footer={null}
      >
        {summaryKeyModalRecord && (
          <BcCalculationDetails
            record={summaryKeyModalRecord}
            getDbRate={(code) => nettRateMap[code] ?? 0}
          />
        )}
      </Modal>

      <Modal
        title="SummaryKey — Live Value (not yet in BOQ export)"
        open={!!extraSummaryKeyRecord}
        onCancel={() => setExtraSummaryKeyRecord(null)}
        footer={null}
      >
        {extraSummaryKeyRecord && (() => {
          const bc = computeDynamicBcCalculation(extraSummaryKeyRecord, (code) => nettRateMap[code] ?? 0);
          return (
            <>
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="Description">{extraSummaryKeyRecord.description}</Descriptions.Item>
                {extraSummaryKeyRecord.quantity != null && <Descriptions.Item label="Quantity">{extraSummaryKeyRecord.quantity}</Descriptions.Item>}
                {extraSummaryKeyRecord.unit && <Descriptions.Item label="Unit">{formatUnit(extraSummaryKeyRecord.unit)}</Descriptions.Item>}
                {extraSummaryKeyRecord.rate != null && <Descriptions.Item label="Rate">{fmtCurrency(extraSummaryKeyRecord.rate)}</Descriptions.Item>}
                {extraSummaryKeyRecord.resourceCode && <Descriptions.Item label="Resource Code">{extraSummaryKeyRecord.resourceCode}</Descriptions.Item>}
                <Descriptions.Item label="Amount">{fmtCurrency(extraSummaryKeyRecord.amount)}</Descriptions.Item>
              </Descriptions>
              {bc && (
                <div style={{ marginTop: 8, padding: 8, background: '#eff6ff', borderRadius: 4 }}>
                  <Text strong>BCCalculation (backend dbRate-only derivation):</Text>
                  <div style={{ marginTop: 8 }}>
                    {bc.steps.map((step, idx) => <div key={idx}><Text style={{ fontSize: 12, color: '#374151' }}>{step}</Text></div>)}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </Modal>

      {extraItemsApi.modals}
    </div>
  );
};

export default CompoundBillSummary;
